'use client'

import type { ReactNode } from 'react'

import { isServer, QueryClient, QueryClientProvider } from '@tanstack/react-query'

let browserQueryClient: QueryClient | undefined

/**
 * Standard TanStack default-client resolution: a fresh client per request on
 * the server (so per-request cache entries never leak across SSR requests) and
 * a lazily-created singleton in the browser (reused across renders).
 */
function getDefaultQueryClient(): QueryClient {
  if (isServer) return new QueryClient()
  browserQueryClient ??= new QueryClient()
  return browserQueryClient
}

/**
 * Wraps `children` in a react-query provider. Defaults to a package-owned
 * client, but a consumer can pass its own `client` (e.g. an app whose tree
 * already mounts a tuned client above this provider) so the two don't split
 * into separate caches.
 */
export function ReactQueryProvider({
  children,
  client
}: {
  children: ReactNode | ReactNode[]
  client?: QueryClient
}) {
  return (
    <QueryClientProvider client={client ?? getDefaultQueryClient()}>{children}</QueryClientProvider>
  )
}
